import type { User } from "@crm/access/authorization/client";
import type { AuthContext } from "@crm/access/types/router";
import { hasWorkbenchRoutePermission } from "./route-permissions";

export type ProtectedRouteDecision =
  | { readonly kind: "loading" }
  | { readonly kind: "redirect-login" }
  | { readonly kind: "redirect-home" }
  | { readonly kind: "forbidden" }
  | { readonly kind: "allow" };

/**
 * Fail-closed owner for the Workbench protected layout.
 *
 * Better Auth's raw session is only evidence that a cookie resolved. Browser
 * access additionally requires the canonical AuthContext identity to have
 * decoded successfully and to represent an active user.
 */
export function decideProtectedRouteAccess(input: {
  readonly isSessionPending: boolean;
  readonly hasSession: boolean;
  readonly auth: AuthContext;
  readonly routePath: string;
}): ProtectedRouteDecision {
  if (input.isSessionPending || input.auth.status === "pending") {
    return { kind: "loading" };
  }

  if (!input.hasSession || input.auth.status !== "authenticated") {
    return { kind: "redirect-login" };
  }

  const user: User = input.auth.user;
  if (user.status !== "active") {
    return { kind: "redirect-login" };
  }

  if (
    !hasWorkbenchRoutePermission(input.auth.permissions, input.routePath)
  ) {
    return user.role === "student" ? { kind: "redirect-home" } : { kind: "forbidden" };
  }

  return { kind: "allow" };
}
